import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { CheckCircle2, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { memo, useState, useEffect, useRef } from 'react'
import type { Message, Conversation } from '@/types/conversations'

// Module-level Map shared across ALL component instances (survives remounts and StrictMode)
const planTimerHandled = new Map<number, boolean>()

function addToTimerMap(messageId: number, value: boolean) {
  // Simple LRU: if map gets too big, clear oldest half
  if (planTimerHandled.size >= 40) {
    const entries = Array.from(planTimerHandled.entries())
    const toKeep = entries.slice(-20) // Keep newest 20
    planTimerHandled.clear()
    toKeep.forEach(([key, val]) => planTimerHandled.set(key, val))
  }
  planTimerHandled.set(messageId, value)
}

interface PlanMessageProps {
  message: Message
  conversation: Conversation
  onApprovePlan: (messageId: number) => Promise<void>
  isProcessing?: boolean
}

export const PlanMessage = memo(function PlanMessage({ message, conversation, onApprovePlan, isProcessing = false }: PlanMessageProps) {
  const { t } = useTranslation('conversations')
  // Use message-level plan_approved for historical state (preserves approved state even when conversation resets)
  const planApproved = message.plan_approved

  // Use live conversation status for current conversation, or snapshot for moved messages
  const isCurrentConversation = message.conversation_id === conversation.id
  const conversationStatus = isCurrentConversation ? conversation.status : message.conversation_status

  const [isApproving, setIsApproving] = useState(false)

  // Determine if plan needs approval (should be expanded and not collapsible)
  const needsApproval = message.requires_approval && !planApproved && !message.form_skipped
  const [isExpanded, setIsExpanded] = useState(needsApproval)

  // Auto-approve timer state (simple setTimeout approach)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isCancelled, setIsCancelled] = useState(planTimerHandled.get(message.id) || false)

  // Stable ref for onApprovePlan to prevent effect re-runs
  const onApprovePlanRef = useRef(onApprovePlan)
  useEffect(() => {
    onApprovePlanRef.current = onApprovePlan
  }, [onApprovePlan])

  const removePlanTags = (content: string): string => {
    return content
      .replace(/<implementation_plan>/gi, '')
      .replace(/<\/implementation_plan>/gi, '')
      .replace(/<level_upgrade_suggestion>.*?<\/level_upgrade_suggestion>/gi, '')
      .replace(/<questions_superseded\s*\/?>/gi, '')
      .replace(/<\/questions_superseded>/gi, '')
      .replace(/<next_step_suggestions>[\s\S]*?<\/next_step_suggestions>/gi, '')
      .replace(/<report>[\s\S]*?<\/report>/gi, '')
      .replace(/<StructuredOutput>[\s\S]*?<\/StructuredOutput>/gi, '')
  }

  const handleApprove = async () => {
    if (!message.can_be_approved) return

    setIsApproving(true)
    try {
      await onApprovePlan(message.id)
      // Don't reset isApproving here - let useEffect handle it when message updates
    } catch (error) {
      console.error('Failed to approve plan:', error)
      setIsApproving(false) // Only reset on error
    }
  }

  // Reset isApproving when the backend confirms the approval
  useEffect(() => {
    if (planApproved || !message.requires_approval) {
      setIsApproving(false)
    }
  }, [planApproved, message.requires_approval])

  // Keep expanded state in sync with approval status
  useEffect(() => {
    if (needsApproval) {
      setIsExpanded(true) // Always expanded when needs approval
    } else {
      setIsExpanded(false) // Collapse when approved or skipped
    }
  }, [needsApproval])

  // Auto-approve after 30 seconds (simple setTimeout - fires once)
  useEffect(() => {
    // Clear any existing timeout first
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }

    // Don't start timer if any of these conditions are true
    if (!message.can_be_approved || planApproved || message.form_skipped || !message.requires_approval || isApproving || isProcessing || planTimerHandled.get(message.id)) {
      return
    }

    timeoutIdRef.current = setTimeout(() => {
      // Double-check that timer hasn't been handled before approving
      if (planTimerHandled.get(message.id)) {
        return
      }
      addToTimerMap(message.id, true)
      setIsApproving(true)
      onApprovePlanRef.current(message.id).catch((error) => {
        console.error('Auto-approve failed:', error)
        setIsApproving(false)
      })
    }, 30000) // 30 seconds

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }
    }
  }, [message.can_be_approved, planApproved, message.form_skipped, message.requires_approval, message.id, isApproving, isProcessing])

  return (
    <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
      <div className="flex flex-col gap-2 min-w-0 flex-1 max-w-full">
        <Card className="border-primary/20 bg-muted/50 p-4 min-w-0 max-w-full">
          {needsApproval ? (
            // Non-collapsible header for plans requiring approval
            <div className="flex items-start gap-2 mb-3 min-w-0">
              <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-foreground mb-1">
                  {t('chat.implementationPlan')}
                </h3>
                <Badge variant="outline" className="text-xs">
                  {t('chat.planningMode')}
                </Badge>
              </div>
            </div>
          ) : (
            // Collapsible button for approved/skipped plans
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-start gap-2 mb-3 min-w-0 w-full text-left hover:opacity-80 transition-opacity"
            >
              <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-foreground mb-1">
                  {t('chat.implementationPlan')}
                </h3>
                <Badge variant="outline" className="text-xs">
                  {t('chat.planningMode')}
                </Badge>
              </div>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              )}
            </button>
          )}

          {isExpanded && (
            <div className="prose prose-sm dark:prose-invert max-w-none mb-4 text-sm [overflow-wrap:anywhere]">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {removePlanTags(message.content)}
              </ReactMarkdown>
            </div>
          )}

          {conversationStatus === 'in_progress' && !planApproved && !message.form_skipped && message.requires_approval && message.can_be_approved && (
            <Button
              onClick={() => {
                if (!isCancelled) {
                  // Cancel auto-approve
                  addToTimerMap(message.id, true)
                  setIsCancelled(true)
                  if (timeoutIdRef.current) {
                    clearTimeout(timeoutIdRef.current)
                    timeoutIdRef.current = null
                  }
                } else {
                  // Manual approve
                  handleApprove()
                }
              }}
              disabled={isApproving || isProcessing}
              variant={isCancelled ? "default" : "outline"}
              className="w-full gap-2 relative overflow-hidden"
            >
              {/* Unfilled background */}
              {!isCancelled && !isApproving && (
                <div className="absolute inset-0 bg-primary/5" />
              )}
              {/* Progress fill with CSS animation */}
              {!isCancelled && !isApproving && (
                <div
                  className="absolute inset-0 bg-primary animate-[fillProgress_30s_linear_forwards]"
                  style={{ transformOrigin: 'left' }}
                />
              )}
              <CheckCircle2 className="h-4 w-4 relative z-10" />
              <span className="relative z-10">
                {isApproving
                  ? t('chat.approving')
                  : isCancelled
                  ? t('chat.approvePlan')
                  : t('chat.autoApproving', { seconds: 30 })}
              </span>
            </Button>
          )}

          {(planApproved || !message.requires_approval) && (
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              <span>{t('chat.planApproved')}</span>
            </div>
          )}

          {message.form_skipped && !planApproved && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{t('chat.planSkipped')}</span>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
})
